import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FaClock, FaEuroSign, FaImage, FaPaperPlane } from 'react-icons/fa'
import { stcService } from '../services/stcService'
import { pcrsService } from '../services/pcrsService'
import { patientService } from '../services/patientService'
import { userService } from '../services/userService'
import type { StcBatchRun, StcRecord, StcRecordStatus, StcRecordsSortBy, StcRecordsSortDirection } from '../types/stc'
import type { User } from '../types/user'
import { formatDate } from '../utils/locale'
import { formatUserName, formatDoctorNameForClaim } from '../utils/name'
import { isEligibleForPcrsRefresh } from '../utils/pcrsRefreshEligibility'
import { Modal } from '../components/Modal'
import { StcSigning } from './StcSigning'
import './StcReport.css'

const STATUS_OPTIONS: { value: '' | StcRecordStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'signed', label: 'Signed' },
  { value: 'queued', label: 'Queued' },
  { value: 'to_pay', label: 'To Pay' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'rejected', label: 'Rejected' },
]

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const SORTABLE_COLUMNS: { key: StcRecordsSortBy; label: string }[] = [
  { key: 'patientName', label: 'Patient' },
  { key: 'consultationDate', label: 'Consultation date' },
  { key: 'processedAt', label: 'Processed' },
]

const REJECTION_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'incorrect_patient_details', label: 'Incorrect patient details (GMS/PPS mismatch)' },
  { value: 'duplicate_claim', label: 'Duplicate claim' },
  { value: 'patient_not_eligible', label: 'Patient not eligible for GMS' },
  { value: 'wrong_fee_code', label: 'Wrong fee code / special service' },
  { value: 'missing_signature', label: 'Missing or incorrect signature' },
  { value: 'wrong_consultation_date', label: 'Wrong consultation date' },
  { value: 'patient_attended_elsewhere', label: 'Patient attended elsewhere' },
  { value: 'form_completed_in_error', label: 'Form completed in error' },
  { value: 'other', label: 'Other (specify below)' },
]

const CLAIM_STATUS_FOR_REIMBURSEMENT: ReadonlySet<StcRecordStatus> = new Set(['to_pay', 'paid'])
const CLAIM_STATUS_FOR_ESTIMATE: ReadonlySet<StcRecordStatus> = new Set(['signed', 'queued', 'to_pay', 'paid'])
const READ_ONLY_STATUSES: ReadonlySet<StcRecordStatus> = new Set(['to_pay', 'paid', 'rejected', 'failed'])
const ESTIMATED_CLAIM_TOTAL_TOOLTIP =
  'Estimated claim total includes signed, queued, To Pay, and Paid claims.'
const ESTIMATED_REIMBURSABLE_TOOLTIP =
  'Estimated reimbursable total includes To Pay and Paid claims only.'

const REFRESH_PCRS_STATUS_TOOLTIP = [
  'Queries SSPCRS for each claim’s current status (To Pay, Paid, Rejected) and updates local records.',
  'Eligibility: Only claims whose consultation month is before the current month (PCRS pays on the 15th for the previous month).',
  'Processing: Up to 20 claims per request, with short delays between inquiries to avoid rate limiting.',
  'Scheduled: An automatic refresh runs on the 16th of each month for eligible claims.',
].join(' ')

const SPECIAL_SERVICE_FEES: Record<string, number> = {
  A: 24.8,
  AB: 70.0,
  AC: 50.0,
  AD: 60.0,
  AL: 100.0,
  AM: 100.0,
  B: 50.0,
  C: 24.8,
  CF: 55.0,
  CG: 100.0,
  CH: 160.0,
  CI: 110.0,
  CJ: 50.0,
  CK: 55.0,
  CL: 55.0,
  CM: 160.0,
  CN: 50.0,
  CO: 100.0,
  CQ: 110.0,
  D: 24.8,
  F: 24.8,
  G: 24.8,
  H: 24.8,
  J: 24.8,
  K: 37.21,
  L: 60.0,
  M: 62.02,
  X: 24.8,
  Y: 37.21,
  Z: 24.8,
}

function normalizeFeeCode(value?: string | null): string {
  return (value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().trim()
}

function getFeeInfo(record: StcRecord): { code: string | null; amount: number | null } {
  if (record.feeCode && SPECIAL_SERVICE_FEES[record.feeCode]) {
    return { code: record.feeCode, amount: SPECIAL_SERVICE_FEES[record.feeCode] }
  }
  if (typeof record.estimatedFee === 'number' && record.estimatedFee > 0) {
    return { code: record.feeCode || null, amount: record.estimatedFee }
  }

  const raw = String(record.formData?.specialService || record.formData?.micsService || '').trim()
  if (!raw || raw === '0') return { code: null, amount: null }

  const direct = normalizeFeeCode(raw)
  if (SPECIAL_SERVICE_FEES[direct]) {
    return { code: direct, amount: SPECIAL_SERVICE_FEES[direct] }
  }

  // Some historical records may store the full label ("K - Nebuliser...")
  const token = raw.split(/[\s\-/:]+/).find(Boolean)?.toUpperCase() || ''
  if (SPECIAL_SERVICE_FEES[token]) {
    return { code: token, amount: SPECIAL_SERVICE_FEES[token] }
  }

  return { code: null, amount: null }
}

function formatEuro(value: number): string {
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(value)
}

function toLocalDateInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function formatPeriodDate(value: string): string {
  const raw = String(value || '').trim()
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!iso) return raw
  return `${iso[3]}-${iso[2]}-${iso[1]}`
}

function getConsultationDateForReport(record: StcRecord): string {
  const dateValue =
    record.formType === 'MICS'
      ? record.formData?.micsClaimDate ?? record.formData?.date ?? record.createdAt ?? ''
      : record.formData?.date ?? record.createdAt ?? ''
  return String(dateValue).trim()
}

function getDateOfBirthForReport(record: StcRecord, patientDobById: Record<string, string>): string {
  const formDob = String(record.formData?.dateOfBirth || '').trim()
  if (formDob) return formDob
  if (!record.patientId) return ''
  return String(patientDobById[record.patientId] || '').trim()
}

function getClaimSummaryUrl(record: StcRecord): string | null {
  const explicit = String(record.lastSubmissionClaimSummaryUrl || '').trim()
  if (explicit) return explicit

  const claimNumber = String(record.claimNumber || '').trim()
  if (!claimNumber) return null
  return buildClaimSummaryUrlFromClaimNumber(claimNumber)
}

function buildClaimSummaryUrlFromClaimNumber(claimNumber: string): string {
  return `https://secure.sspcrs.ie/stcss/sec/claim/${encodeURIComponent(claimNumber)}?mode=saved&previousMode=KBPS`
}

function formatStcStatus(status: StcRecordStatus): string {
  if (status === 'to_pay') return 'To Pay'
  if (status === 'paid') return 'Paid'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

type QuickDateKey =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-year'
  | 'last-year'
  | 'clear'

const QUICK_DATE_OPTIONS: Array<{ key: QuickDateKey; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this-week', label: 'This week' },
  { key: 'last-week', label: 'Last week' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-year', label: 'This year' },
  { key: 'last-year', label: 'Last year' },
  { key: 'clear', label: 'Clear' },
]

export function StcReport() {
  const [statusFilter, setStatusFilter] = useState<'' | StcRecordStatus>('signed')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortBy, setSortBy] = useState<StcRecordsSortBy>('consultationDate')
  const [sortDirection, setSortDirection] = useState<StcRecordsSortDirection>('desc')
  const [selectedRecord, setSelectedRecord] = useState<StcRecord | null>(null)
  const [formPngLoadingId, setFormPngLoadingId] = useState<string | null>(null)
  const [submitPendingRecord, setSubmitPendingRecord] = useState<StcRecord | null>(null)
  const [submitModalRecord, setSubmitModalRecord] = useState<StcRecord | null>(null)
  const [submitSelectedProviderId, setSubmitSelectedProviderId] = useState<string>('')
  const [manualSubmitModalRecord, setManualSubmitModalRecord] = useState<StcRecord | null>(null)
  const [manualClaimNumber, setManualClaimNumber] = useState('')
  const [manualFormNumber, setManualFormNumber] = useState('')
  const [rejectModalRecord, setRejectModalRecord] = useState<StcRecord | null>(null)
  const [resetToSignedModalRecord, setResetToSignedModalRecord] = useState<StcRecord | null>(null)
  const [rejectReasonPreset, setRejectReasonPreset] = useState('')
  const [rejectReasonOther, setRejectReasonOther] = useState('')
  const [rejectReasonDetails, setRejectReasonDetails] = useState('')
  const [batchModalOpen, setBatchModalOpen] = useState(false)
  const [batchSelectedProviderId, setBatchSelectedProviderId] = useState<string>('')
  const [activeBatchRunId, setActiveBatchRunId] = useState<string | null>(null)
  const [activeQuickDate, setActiveQuickDate] = useState<QuickDateKey | null>(null)
  const resolveSubmittedRef = useRef(false)
  const queryClient = useQueryClient()

  const applyQuickDate = (key: QuickDateKey) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    let start: string | null = null
    let end: string | null = null

    switch (key) {
      case 'today': {
        start = toLocalDateInput(now)
        end = start
        break
      }
      case 'yesterday': {
        const d = new Date(now)
        d.setDate(d.getDate() - 1)
        start = toLocalDateInput(d)
        end = start
        break
      }
      case 'this-week': {
        start = toLocalDateInput(startOfWeekMonday(now))
        end = toLocalDateInput(now)
        break
      }
      case 'last-week': {
        const thisWeekStart = startOfWeekMonday(now)
        const lastWeekStart = new Date(thisWeekStart)
        lastWeekStart.setDate(lastWeekStart.getDate() - 7)
        const lastWeekEnd = new Date(thisWeekStart)
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1)
        start = toLocalDateInput(lastWeekStart)
        end = toLocalDateInput(lastWeekEnd)
        break
      }
      case 'this-month': {
        const first = new Date(now.getFullYear(), now.getMonth(), 1)
        start = toLocalDateInput(first)
        end = toLocalDateInput(now)
        break
      }
      case 'last-month': {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const last = new Date(now.getFullYear(), now.getMonth(), 0)
        start = toLocalDateInput(first)
        end = toLocalDateInput(last)
        break
      }
      case 'this-year': {
        const first = new Date(now.getFullYear(), 0, 1)
        start = toLocalDateInput(first)
        end = toLocalDateInput(now)
        break
      }
      case 'last-year': {
        const first = new Date(now.getFullYear() - 1, 0, 1)
        const last = new Date(now.getFullYear() - 1, 11, 31)
        start = toLocalDateInput(first)
        end = toLocalDateInput(last)
        break
      }
      case 'clear':
      default:
        break
    }

    setStartDate(start || '')
    setEndDate(end || '')
    setActiveQuickDate(key === 'clear' ? null : key)
    setPage(1)
  }

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  const { data: submitPendingPatient, isFetched: submitPendingPatientFetched } = useQuery({
    queryKey: ['patient', submitPendingRecord?.patientId],
    queryFn: () => patientService.getById(submitPendingRecord?.patientId || ''),
    enabled: !!submitPendingRecord?.patientId,
  })

  const { data: allPatients = [] } = useQuery({
    queryKey: ['patients', 'stc-report-dob-fallback'],
    queryFn: async () => {
      const data = await patientService.getAllPatients()
      return data
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })

  const openFormPng = async (recordId: string) => {
    setFormPngLoadingId(recordId)
    try {
      const url = await stcService.getFormPngUrl(recordId)
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setFormPngLoadingId(null)
    }
  }

function formatProcessedPeriod(record: StcRecord): string {
  const fromInquiry = String(record.lastInquiryProcessedPeriod || '').trim()
  if (/^\d{6}$/.test(fromInquiry)) return fromInquiry
  if (!record.processedAt) return ''
  const date = new Date(record.processedAt)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${year}${month}`
}

  const { data: paginatedData, isLoading, error, refetch: refetchRecords } = useQuery({
    queryKey: ['stc-records', statusFilter || undefined, startDate || undefined, endDate || undefined, page, pageSize, sortBy, sortDirection],
    queryFn: () =>
      stcService.getRecords({
        ...(statusFilter && { status: statusFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        page,
        pageSize,
        sortBy,
        sortDirection,
      }),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })

  const { data: totalsRecordsData, refetch: refetchTotals } = useQuery({
    queryKey: ['stc-records-fee-totals', statusFilter || undefined, startDate || undefined, endDate || undefined],
    queryFn: async () => {
      const pageSizeForTotals = 200
      const first = await stcService.getRecords({
        ...(statusFilter && { status: statusFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        page: 1,
        pageSize: pageSizeForTotals,
        sortBy: 'createdAt',
        sortDirection: 'desc',
      })

      let allItems = [...(first.items || [])]
      if ((first.totalPages || 1) > 1) {
        const pages = Array.from({ length: first.totalPages - 1 }, (_, i) => i + 2)
        const rest = await Promise.all(
          pages.map((p) =>
            stcService.getRecords({
              ...(statusFilter && { status: statusFilter }),
              ...(startDate && { startDate }),
              ...(endDate && { endDate }),
              page: p,
              pageSize: pageSizeForTotals,
              sortBy: 'createdAt',
              sortDirection: 'desc',
            })
          )
        )
        allItems = allItems.concat(...rest.map((r) => r.items || []))
      }
      return allItems
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })

  const refreshReport = async () => {
    await Promise.all([
      refetchRecords(),
      refetchTotals(),
      queryClient.invalidateQueries({ queryKey: ['patients', 'stc-report-dob-fallback'] }),
    ])
  }

  const handleSort = (column: StcRecordsSortBy) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
    setPage(1)
  }

  const items = paginatedData?.items ?? []
  const patientDobById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const patient of allPatients) {
      if (!patient?.id) continue
      map[patient.id] = String(patient.dateOfBirth || '').trim()
    }
    return map
  }, [allPatients])
  const totalCount = paginatedData?.totalCount ?? 0
  const totalPages = paginatedData?.totalPages ?? 1
  const totalsItems = totalsRecordsData ?? items

  const feeSummary = useMemo(() => {
    let matchedItems = 0
    let totalEstimatedShown = 0
    let reimbursableCount = 0
    let reimbursableTotal = 0
    let estimateEligibleCount = 0
    let estimateEligibleTotal = 0
    for (const record of totalsItems) {
      const { amount } = getFeeInfo(record)
      if (amount == null) continue
      matchedItems += 1
      totalEstimatedShown += amount
      if (CLAIM_STATUS_FOR_ESTIMATE.has(record.status)) {
        estimateEligibleCount += 1
        estimateEligibleTotal += amount
      }
      if (CLAIM_STATUS_FOR_REIMBURSEMENT.has(record.status)) {
        reimbursableCount += 1
        reimbursableTotal += amount
      }
    }
    return {
      matchedItems,
      totalEstimatedShown,
      estimateEligibleCount,
      estimateEligibleTotal,
      reimbursableCount,
      reimbursableTotal,
    }
  }, [totalsItems])

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  const pageNumbers = useMemo(() => {
    const maxButtons = 7
    const half = Math.floor(maxButtons / 2)
    let start = Math.max(1, page - half)
    let end = Math.min(totalPages, start + maxButtons - 1)
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1)
    }
    const pages: number[] = []
    for (let i = start; i <= end; i += 1) {
      pages.push(i)
    }
    return pages
  }, [page, totalPages])

  const pageSummary = useMemo(() => {
    if (totalCount === 0) return 'No results'
    const startIndex = (page - 1) * pageSize + 1
    const endIndex = Math.min(totalCount, page * pageSize)
    return `${startIndex}-${endIndex} of ${totalCount}`
  }, [page, pageSize, totalCount])

  useEffect(() => {
    setPage(1)
  }, [statusFilter, startDate, endDate])

  const submitFiredForRef = useRef<string | null>(null)
  const submitMutation = useMutation({
    mutationFn: async ({ id, docNum, docNam }: { id: string; docNum: string; docNam: string }) => {
      const submitResult = await pcrsService.submitClaim({ stcRecordId: id, docNum, docNam })
      return stcService.submitClaim(id, {
        claimNumber: submitResult.claimNumber,
        formNumber: submitResult.formNumber,
        claimSummaryUrl: submitResult.claimSummaryUrl,
      })
    },
    onSuccess: () => {
      submitFiredForRef.current = null
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      setSubmitModalRecord(null)
      setSubmitPendingRecord(null)
      setSubmitSelectedProviderId('')
    },
    onError: () => {
      submitFiredForRef.current = null
      // Ensure backend-updated failure status/details become visible immediately.
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })

  const queueMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'queued'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })
  const dequeueMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'signed'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason?: string }) =>
      stcService.updateStatus(id, 'rejected', { rejectionReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      setRejectModalRecord(null)
      setRejectReasonPreset('')
      setRejectReasonOther('')
      setRejectReasonDetails('')
    },
  })

  /** Return record to signed from rejected or failed (after issue understood / corrected). */
  const resetToSignedMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'signed'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })
  const refreshPcrsStatusesMutation = useMutation({
    mutationFn: async (recordIds: string[]) => pcrsService.refreshClaimStatuses({ stcRecordIds: recordIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      queryClient.invalidateQueries({ queryKey: ['stc-records-fee-totals'] })
      queryClient.invalidateQueries({ queryKey: ['pcrs-refresh-last-run'] })
    },
  })

  const { data: refreshLastRun } = useQuery({
    queryKey: ['pcrs-refresh-last-run'],
    queryFn: () => pcrsService.getRefreshLastRun(),
  })
  const manualSubmitMutation = useMutation({
    mutationFn: async ({ id, claimNumber, formNumber }: { id: string; claimNumber: string; formNumber?: string }) => {
      const trimmedClaimNumber = claimNumber.trim()
      const trimmedFormNumber = String(formNumber || '').trim()
      return stcService.submitClaim(id, {
        claimNumber: trimmedClaimNumber,
        ...(trimmedFormNumber ? { formNumber: trimmedFormNumber } : {}),
        claimSummaryUrl: buildClaimSummaryUrlFromClaimNumber(trimmedClaimNumber),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      queryClient.invalidateQueries({ queryKey: ['stc-records-fee-totals'] })
      setManualSubmitModalRecord(null)
      setManualClaimNumber('')
      setManualFormNumber('')
    },
  })

  const providersWithGms = useMemo(
    () => (clinicalStaff ?? []).filter((s): s is User & { gmsNumber: string } => !!s.gmsNumber?.trim()),
    [clinicalStaff]
  )
  const queuedRecords = useMemo(
    () => totalsItems.filter((r) => r.formType !== 'MICS' && r.status === 'queued' && !r.lockBatchRunId),
    [totalsItems]
  )
  const toPayRecords = useMemo(
    () => totalsItems.filter((r) => r.status === 'to_pay' && !!String(r.claimNumber || '').trim()),
    [totalsItems]
  )
  const eligibleToPayRecords = useMemo(
    () => toPayRecords.filter((r) => isEligibleForPcrsRefresh(r)),
    [toPayRecords]
  )

  const { data: latestBatchRuns } = useQuery({
    queryKey: ['stc-batch-runs'],
    queryFn: () => stcService.listBatchRuns(5),
    refetchInterval: 5000,
  })

  const latestBatchRun = latestBatchRuns?.[0] ?? null
  const batchRunToTrack = useMemo<StcBatchRun | null>(() => {
    if (activeBatchRunId) {
      return latestBatchRuns?.find((run) => run.id === activeBatchRunId) ?? null
    }
    return latestBatchRun
  }, [activeBatchRunId, latestBatchRun, latestBatchRuns])

  const { data: activeBatchRun } = useQuery({
    queryKey: ['stc-batch-run', batchRunToTrack?.id],
    queryFn: () => stcService.getBatchRun(batchRunToTrack!.id),
    enabled: !!batchRunToTrack?.id,
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 3000 : false,
  })

  useEffect(() => {
    if (!activeBatchRun?.id) return

    if (activeBatchRun.status === 'running') {
      const interval = window.setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      }, 3000)
      return () => window.clearInterval(interval)
    }

    queryClient.invalidateQueries({ queryKey: ['stc-records'] })
  }, [activeBatchRun?.id, activeBatchRun?.status, queryClient])

  const startBatchMutation = useMutation({
    mutationFn: async ({ docNum, docNam }: { docNum: string; docNam: string }) => {
      const recordIds = queuedRecords.map((r) => r.id)
      return stcService.startBatchRun({
        recordIds,
        submitDocNum: docNum,
        submitDocNam: docNam,
      })
    },
    onSuccess: (result) => {
      setActiveBatchRunId(result.batchRunId)
      setBatchModalOpen(false)
      setBatchSelectedProviderId('')
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      queryClient.invalidateQueries({ queryKey: ['stc-batch-runs'] })
    },
  })

  useEffect(() => {
    const canResolvePatient = !submitPendingRecord?.patientId || submitPendingPatientFetched
    if (!submitPendingRecord || !clinicalStaff || !canResolvePatient || resolveSubmittedRef.current) return

    resolveSubmittedRef.current = true
    const record = submitPendingRecord
    const patient = submitPendingPatient
    const registeredGpId = patient?.practiceRegistration?.registeredGpId
    const assigned = registeredGpId
      ? clinicalStaff.find((s) => s.id === registeredGpId)
      : null

    if (assigned?.gmsNumber?.trim()) {
      if (submitFiredForRef.current === record.id) return
      submitFiredForRef.current = record.id
      submitMutation.mutate({
        id: record.id,
        docNum: assigned.gmsNumber.trim(),
        docNam: formatDoctorNameForClaim(assigned),
      })
      setSubmitPendingRecord(null)
    } else {
      setSubmitModalRecord(record)
      setSubmitPendingRecord(null)
    }
  }, [submitPendingRecord, submitPendingPatient, submitPendingPatientFetched, clinicalStaff])

  useEffect(() => {
    if (!submitPendingRecord) resolveSubmittedRef.current = false
  }, [submitPendingRecord])

  const exportCsv = () => {
    const headers = [
      'Patient',
      'GMS',
      'PPS',
      'Date of birth',
      'Consultation date',
      'Form type',
      'Status',
      'Claim number',
      'Fee code',
      'Estimated fee (EUR)',
      'Processed',
    ]
    const rows = totalsItems.map((r) => [
      ...(function () {
        const fee = getFeeInfo(r)
        return [
      r.patientName || r.patientId,
      r.gmsNumber ?? '',
      r.ppsNumber ?? '',
      getDateOfBirthForReport(r, patientDobById) ? formatDate(getDateOfBirthForReport(r, patientDobById)) : '',
      getConsultationDateForReport(r) ? formatDate(getConsultationDateForReport(r)) : '',
      r.formType,
      r.status,
      r.claimNumber ?? '',
      fee.code ?? '',
      fee.amount != null ? fee.amount.toFixed(2) : '',
      formatProcessedPeriod(r),
        ]
      })(),
    ])
    const footerRows = [
      ['Estimated claim total (signed/queued/to_pay/paid)', '', '', '', '', '', '', '', '', feeSummary.estimateEligibleTotal.toFixed(2), ''],
      ['Estimated reimbursable total (to_pay/paid)', '', '', '', '', '', '', '', '', feeSummary.reimbursableTotal.toFixed(2), ''],
      ['Estimated total shown (all matched fee rows)', '', '', '', '', '', '', '', '', feeSummary.totalEstimatedShown.toFixed(2), ''],
    ]
    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
      ...footerRows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `stc-status-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const queueSignedVisible = async () => {
    const signedRecords = totalsItems.filter((r) => r.formType !== 'MICS' && r.status === 'signed' && !r.lockBatchRunId)
    if (signedRecords.length === 0) return
    await Promise.all(signedRecords.map((r) => queueMutation.mutateAsync(r.id)))
  }

  const dequeueQueuedVisible = async () => {
    if (queuedRecords.length === 0) return
    await Promise.all(queuedRecords.map((r) => dequeueMutation.mutateAsync(r.id)))
  }

  const startQueuedBatch = () => {
    if (providersWithGms.length === 1) {
      setBatchSelectedProviderId(providersWithGms[0].id)
    }
    setBatchModalOpen(true)
  }

  return (
    <div className="stc-report-page" role="main">
      <header className="stc-report-header">
        <h1>STCS/SS Claim Status Report</h1>
        <p className="stc-report-subtitle">View outstanding and processed STC/MICS declarations</p>
      </header>

      <div className="stc-report-filters">
        <div className="filter-group">
          <label htmlFor="stc-filter-status">Status</label>
          <select
            id="stc-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value || '') as '' | StcRecordStatus)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="stc-filter-start">From date</label>
          <input
            id="stc-filter-start"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value)
              setActiveQuickDate(null)
            }}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="stc-filter-end">To date</label>
          <input
            id="stc-filter-end"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value)
              setActiveQuickDate(null)
            }}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="stc-filter-page-size">Per page</label>
          <select
            id="stc-filter-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
              setPage(1)
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="stc-report-bulk-actions" role="group" aria-label="Report actions">
          <button type="button" className="btn-secondary stc-report-bulk-btn" onClick={() => window.print()}>
            Print
          </button>
          <button type="button" className="btn-secondary stc-report-bulk-btn" onClick={refreshReport}>
            Refresh
          </button>
          <button type="button" className="btn-primary stc-report-bulk-btn" onClick={exportCsv} disabled={totalsItems.length === 0}>
            Export CSV
          </button>
          <button
            type="button"
            className="btn-secondary stc-report-bulk-btn"
            onClick={queueSignedVisible}
            disabled={queueMutation.isPending || !totalsItems.some((r) => r.formType !== 'MICS' && r.status === 'signed' && !r.lockBatchRunId)}
          >
            {queueMutation.isPending ? 'Queueing...' : 'Queue Signed'}
          </button>
          <button
            type="button"
            className="btn-secondary stc-report-bulk-btn"
            onClick={dequeueQueuedVisible}
            disabled={dequeueMutation.isPending || queuedRecords.length === 0}
          >
            {dequeueMutation.isPending ? 'Dequeuing...' : `Dequeue Queued (${queuedRecords.length})`}
          </button>
          <button
            type="button"
            className="btn-primary stc-report-bulk-btn"
            onClick={startQueuedBatch}
            disabled={startBatchMutation.isPending || queuedRecords.length === 0}
          >
            {startBatchMutation.isPending ? 'Starting batch…' : `Run Queued Batch (${queuedRecords.length})`}
          </button>
          <span className="stc-report-refresh-pcrs-wrap">
            <button
              type="button"
              className="btn-secondary stc-report-bulk-btn"
              onClick={() => refreshPcrsStatusesMutation.mutate(eligibleToPayRecords.map((r) => r.id))}
              disabled={refreshPcrsStatusesMutation.isPending || eligibleToPayRecords.length === 0}
              title={
                eligibleToPayRecords.length === 0 && toPayRecords.length > 0
                  ? 'No eligible claims to refresh (PCRS pays on the 15th for the previous month)'
                  : REFRESH_PCRS_STATUS_TOOLTIP
              }
            >
              {refreshPcrsStatusesMutation.isPending ? 'Refreshing PCRS…' : `Refresh PCRS Status (${eligibleToPayRecords.length})`}
            </button>
            <button
              type="button"
              className="stc-report-help-icon stc-report-bulk-help"
              title={REFRESH_PCRS_STATUS_TOOLTIP}
              aria-label={REFRESH_PCRS_STATUS_TOOLTIP}
            >
              ?
            </button>
          </span>
          {eligibleToPayRecords.length === 0 && toPayRecords.length > 0 && (
            <span className="stc-report-refresh-hint" role="status">
              No eligible claims to refresh (PCRS pays on the 15th for the previous month)
            </span>
          )}
        </div>
        <div className="stc-report-quick-dates" role="group" aria-label="Quick date filters">
          {QUICK_DATE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`stc-report-quick-date-btn ${activeQuickDate === opt.key ? 'active' : ''}`}
              onClick={() => applyQuickDate(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p>Loading…</p>}
      {error && <p className="stc-report-error">Failed to load records.</p>}
      {submitMutation.isError && (
        <p className="stc-report-error" role="alert">
          Failed to submit claim. Please try again.
        </p>
      )}
      {startBatchMutation.isError && (
        <p className="stc-report-error" role="alert">
          {(startBatchMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
            (startBatchMutation.error instanceof Error ? startBatchMutation.error.message : 'Failed to start batch run')}
        </p>
      )}
      {refreshPcrsStatusesMutation.isError && (
        <p className="stc-report-error" role="alert">
          {(refreshPcrsStatusesMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
            (refreshPcrsStatusesMutation.error instanceof Error ? refreshPcrsStatusesMutation.error.message : 'Failed to refresh PCRS statuses')}
        </p>
      )}
      {manualSubmitMutation.isError && (
        <p className="stc-report-error" role="alert">
          {(manualSubmitMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
            (manualSubmitMutation.error instanceof Error ? manualSubmitMutation.error.message : 'Failed to mark claim as submitted')}
        </p>
      )}
      {activeBatchRun && (
        <section className="stc-report-summary" aria-label="Batch run progress">
          <div className="stc-report-summary-item">
            <span>Batch status</span>
            <strong>{activeBatchRun.status}</strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Progress</span>
            <strong>
              {activeBatchRun.processedItems}/{activeBatchRun.totalItems}
            </strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Submitted</span>
            <strong>{activeBatchRun.submittedCount}</strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Failed</span>
            <strong>{activeBatchRun.failedCount}</strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Skipped</span>
            <strong>{activeBatchRun.skippedCount}</strong>
          </div>
        </section>
      )}

      {!isLoading && !error && (
        <>
          <section className="stc-report-summary" aria-label="Fee summary">
            <div className="stc-report-summary-item">
              <span>Period</span>
              <strong>
                {startDate || endDate
                  ? `${startDate ? formatPeriodDate(startDate) : 'Start'} to ${endDate ? formatPeriodDate(endDate) : 'Today'}`
                  : 'All available dates'}
              </strong>
            </div>
            <div className="stc-report-summary-item">
              <span>Items in period</span>
              <strong>{totalsItems.length}</strong>
            </div>
            <div className="stc-report-summary-item">
              <span>Fee-matched items</span>
              <strong>{feeSummary.matchedItems}</strong>
            </div>
            <div className="stc-report-summary-item">
              <span className="stc-report-label-with-help">
                Estimated claim total
                <button type="button" className="stc-report-help-icon" title={ESTIMATED_CLAIM_TOTAL_TOOLTIP} aria-label={ESTIMATED_CLAIM_TOTAL_TOOLTIP}>
                  ?
                </button>
              </span>
              <strong>{formatEuro(feeSummary.estimateEligibleTotal)}</strong>
            </div>
            <div className="stc-report-summary-item">
              <span className="stc-report-label-with-help">
                Estimated reimbursable total
                <button type="button" className="stc-report-help-icon" title={ESTIMATED_REIMBURSABLE_TOOLTIP} aria-label={ESTIMATED_REIMBURSABLE_TOOLTIP}>
                  ?
                </button>
              </span>
              <strong>{formatEuro(feeSummary.reimbursableTotal)}</strong>
            </div>
            {refreshLastRun?.lastRunAt && (
              <div className="stc-report-summary-item">
                <span>Last auto-refresh</span>
                <strong>
                  {formatDate(refreshLastRun.lastRunAt, 'd MMM yyyy')}
                  {typeof refreshLastRun.updatedCount === 'number' && (
                    <span className="stc-report-last-run-detail">
                      {' · '}
                      {refreshLastRun.updatedCount} claim{refreshLastRun.updatedCount !== 1 ? 's' : ''} updated
                    </span>
                  )}
                </strong>
              </div>
            )}
          </section>

          <div className="stc-report-pagination" role="navigation" aria-label="Report pages">
            <div className="stc-report-page-summary" aria-live="polite">
              {pageSummary}
            </div>
            <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                First
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <div className="stc-report-page-numbers" aria-label="Report list pages">
                {pageNumbers[0] > 1 && (
                  <>
                    <button
                      type="button"
                      className={`stc-report-page-num ${page === 1 ? 'active' : ''}`}
                      onClick={() => setPage(1)}
                    >
                      1
                    </button>
                    {pageNumbers[0] > 2 && <span className="stc-report-page-ellipsis">…</span>}
                  </>
                )}
                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    type="button"
                    className={`stc-report-page-num ${pageNum === page ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <span className="stc-report-page-ellipsis">…</span>
                    )}
                    <button
                      type="button"
                      className={`stc-report-page-num ${page === totalPages ? 'active' : ''}`}
                      onClick={() => setPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              >
                Last
              </button>
          </div>
          <div className="stc-report-table-wrap">
          <table className="stc-report-table" aria-label="STC records (current page)">
            <thead>
              <tr>
                {SORTABLE_COLUMNS.map(({ key, label }) => (
                  <th key={key}>
                    <button
                      type="button"
                      className={`stc-report-th-sort ${sortBy === key ? 'active' : ''}`}
                      onClick={() => handleSort(key)}
                      aria-sort={sortBy === key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {label}
                      {sortBy === key && (
                        <span className="stc-report-sort-icon" aria-hidden>
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </button>
                  </th>
                ))}
                <th>GMS</th>
                <th>PPS</th>
                <th>Date of birth</th>
                <th>Form type</th>
                <th>Status</th>
                <th>Claim #</th>
                <th>Fee code</th>
                <th>Estimated fee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={12}>No records match the filters.</td>
                </tr>
              ) : (
                items.map((r) => {
                  const fee = getFeeInfo(r)
                  const isMics = r.formType === 'MICS'
                  return (
                  <tr
                    key={r.id}
                    className="stc-report-row-clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedRecord(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelectedRecord(r)
                      }
                    }}
                  >
                    <td>{r.patientName || r.patientId}</td>
                    <td>{getConsultationDateForReport(r) ? formatDate(getConsultationDateForReport(r)) : '—'}</td>
                    <td>{formatProcessedPeriod(r) || '—'}</td>
                    <td>{r.gmsNumber ?? '—'}</td>
                    <td>{r.ppsNumber ?? '—'}</td>
                    <td>{getDateOfBirthForReport(r, patientDobById) ? formatDate(getDateOfBirthForReport(r, patientDobById)) : '—'}</td>
                    <td>{r.formType}</td>
                    <td
                      title={
                        r.status === 'failed' && r.lastSubmissionErrorMessage
                          ? `Failed: ${r.lastSubmissionErrorMessage}`
                          : r.status === 'rejected' && r.rejectionReason
                            ? `Rejected: ${r.rejectionReason}`
                            : r.lastInquiryStatusRaw
                              ? `PCRS status: ${r.lastInquiryStatusRaw}${r.lastInquiryAt ? ` (${formatDate(r.lastInquiryAt)})` : ''}`
                              : r.status === 'rejected'
                                ? 'Rejected'
                                : undefined
                      }
                    >
                      {formatStcStatus(r.status)}
                      {r.status === 'to_pay' && !!String(r.claimNumber || '').trim() && (
                        <span
                          className={
                            isEligibleForPcrsRefresh(r)
                              ? 'stc-report-status-eligible'
                              : 'stc-report-status-ineligible'
                          }
                          title={
                            isEligibleForPcrsRefresh(r)
                              ? 'Eligible for status refresh (consultation month before current month)'
                              : 'Not yet payable (PCRS pays on the 15th for the previous month)'
                          }
                        >
                          {isEligibleForPcrsRefresh(r) ? (
                            <FaEuroSign className="stc-report-status-icon" aria-hidden />
                          ) : (
                            <FaClock className="stc-report-status-icon" aria-hidden />
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      {r.claimNumber ? (
                        getClaimSummaryUrl(r) ? (
                          <a
                            href={getClaimSummaryUrl(r)!}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={r.formNumber ? `Form No: ${r.formNumber}` : 'Form No: unavailable'}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.claimNumber}
                          </a>
                        ) : (
                          <span title={r.formNumber ? `Form No: ${r.formNumber}` : 'Form No: unavailable'}>
                            {r.claimNumber}
                          </span>
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{fee.code ?? '—'}</td>
                    <td className="stc-report-fee">{fee.amount != null ? formatEuro(fee.amount) : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="stc-report-actions">
                        {r.formPngS3Key && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => openFormPng(r.id)}
                            disabled={formPngLoadingId === r.id}
                            aria-label={formPngLoadingId === r.id ? 'Opening…' : `View saved form for ${r.patientName || r.patientId}`}
                            title="View saved form"
                          >
                            <FaImage aria-hidden />
                          </button>
                        )}
                        {!isMics && (r.status === 'signed' || r.status === 'queued') && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => setSubmitPendingRecord(r)}
                            disabled={submitMutation.isPending || !!r.lockBatchRunId || (!!submitPendingRecord && submitPendingRecord.id === r.id)}
                            aria-label={
                              submitMutation.isPending && submitMutation.variables?.id === r.id
                                ? 'Submitting…'
                                : submitPendingRecord?.id === r.id
                                  ? 'Checking…'
                                  : `Submit claim for ${r.patientName || r.patientId || 'patient'}`
                            }
                            title="Submit claim"
                          >
                            <FaPaperPlane aria-hidden />
                          </button>
                        )}
                        {(r.status === 'signed' || r.status === 'queued') && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => {
                              setManualSubmitModalRecord(r)
                              setManualClaimNumber(String(r.claimNumber || '').trim())
                              setManualFormNumber(String(r.formNumber || '').trim())
                            }}
                            disabled={manualSubmitMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Mark claim as submitted manually for ${r.patientName || r.patientId || 'patient'}`}
                            title="Mark submitted manually"
                          >
                            M
                          </button>
                        )}
                        {!isMics && r.status === 'signed' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => queueMutation.mutate(r.id)}
                            disabled={queueMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Queue claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Queue claim"
                          >
                            Q
                          </button>
                        )}
                        {!isMics && r.status === 'queued' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => dequeueMutation.mutate(r.id)}
                            disabled={dequeueMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Dequeue claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Dequeue claim"
                          >
                            D
                          </button>
                        )}
                        {r.status === 'signed' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              setRejectModalRecord(r)
                              setRejectReasonPreset('')
                              setRejectReasonOther('')
                              setRejectReasonDetails('')
                            }}
                            disabled={rejectMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Reject claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Reject claim"
                          >
                            R
                          </button>
                        )}
                        {r.status === 'rejected' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => resetToSignedMutation.mutate(r.id)}
                            disabled={resetToSignedMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Re-accept claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Re-accept claim"
                          >
                            A
                          </button>
                        )}
                        {r.status === 'failed' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => setResetToSignedModalRecord(r)}
                            disabled={resetToSignedMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Return claim to signed for ${r.patientName || r.patientId || 'patient'}`}
                            title="Return to signed (after resolving submission failure)"
                          >
                            S
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
          <div className="stc-report-print-all" aria-hidden="true">
            <table className="stc-report-table" aria-label="STC records (all filtered)">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Consultation date</th>
                  <th>Processed</th>
                  <th>GMS</th>
                  <th>PPS</th>
                  <th>Date of birth</th>
                  <th>Form type</th>
                  <th>Status</th>
                  <th>Claim #</th>
                  <th>Fee code</th>
                  <th>Estimated fee</th>
                </tr>
              </thead>
              <tbody>
                {totalsItems.length === 0 ? (
                  <tr>
                    <td colSpan={11}>No records match the filters.</td>
                  </tr>
                ) : (
                  totalsItems.map((r) => {
                    const fee = getFeeInfo(r)
                    return (
                      <tr key={r.id}>
                        <td>{r.patientName || r.patientId}</td>
                        <td>{getConsultationDateForReport(r) ? formatDate(getConsultationDateForReport(r)) : '—'}</td>
                        <td>{formatProcessedPeriod(r) || '—'}</td>
                        <td>{r.gmsNumber ?? '—'}</td>
                        <td>{r.ppsNumber ?? '—'}</td>
                        <td>{getDateOfBirthForReport(r, patientDobById) ? formatDate(getDateOfBirthForReport(r, patientDobById)) : '—'}</td>
                        <td>{r.formType}</td>
                        <td>
                          {formatStcStatus(r.status)}
                          {r.status === 'to_pay' && !!String(r.claimNumber || '').trim() && (
                            <span
                              className={
                                isEligibleForPcrsRefresh(r)
                                  ? 'stc-report-status-eligible'
                                  : 'stc-report-status-ineligible'
                              }
                            >
                              {isEligibleForPcrsRefresh(r) ? (
                                <FaEuroSign className="stc-report-status-icon" aria-hidden />
                              ) : (
                                <FaClock className="stc-report-status-icon" aria-hidden />
                              )}
                            </span>
                          )}
                        </td>
                        <td>{r.claimNumber ?? '—'}</td>
                        <td>{fee.code ?? '—'}</td>
                        <td className="stc-report-fee">{fee.amount != null ? formatEuro(fee.amount) : '—'}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {submitModalRecord && (
        <Modal
          isOpen={!!submitModalRecord}
          onClose={() => {
            if (!submitMutation.isPending) {
              setSubmitModalRecord(null)
              setSubmitSelectedProviderId('')
            }
          }}
          title="Submit claim to SSPCRS"
        >
          <p className="stc-report-submit-modal-intro">
            The patient&apos;s assigned GMS doctor does not have a GMS number (or could not be determined). Select a provider with a GMS number to submit the claim under.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const selected = submitSelectedProviderId ? providersWithGms.find((p) => p.id === submitSelectedProviderId) : null
              if (submitModalRecord && selected?.gmsNumber?.trim()) {
                if (submitFiredForRef.current === submitModalRecord.id) return
                submitFiredForRef.current = submitModalRecord.id
                submitMutation.mutate({
                  id: submitModalRecord.id,
                  docNum: selected.gmsNumber.trim(),
                  docNam: formatDoctorNameForClaim(selected),
                })
              }
            }}
            className="stc-report-submit-form"
          >
            <div className="form-group">
              <label htmlFor="submit-provider">Provider (GMS number required)</label>
              <select
                id="submit-provider"
                value={submitSelectedProviderId}
                onChange={(e) => setSubmitSelectedProviderId(e.target.value)}
                required
                disabled={submitMutation.isPending}
                aria-describedby={providersWithGms.length === 0 ? 'submit-provider-empty-hint' : undefined}
              >
                <option value="">Select provider...</option>
                {providersWithGms.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {formatUserName(provider)} (GMS: {provider.gmsNumber})
                  </option>
                ))}
              </select>
              {providersWithGms.length === 0 && (
                <span id="submit-provider-empty-hint" className="form-hint" role="status">
                  No providers with a GMS number. Add a GMS number to a user in Settings.
                </span>
              )}
            </div>
            {submitMutation.isError && (
              <p className="form-error" role="alert">
                {(submitMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
                  (submitMutation.error instanceof Error ? submitMutation.error.message : 'Submission failed. Please try again.')}
              </p>
            )}
            <div className="stc-report-submit-form-actions">
              <button
                type="button"
                onClick={() => {
                  setSubmitModalRecord(null)
                  setSubmitSelectedProviderId('')
                }}
                disabled={submitMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitMutation.isPending || !submitSelectedProviderId || providersWithGms.length === 0}
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit to SSPCRS'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {manualSubmitModalRecord && (
        <Modal
          isOpen={!!manualSubmitModalRecord}
          onClose={() => {
            if (!manualSubmitMutation.isPending) {
              setManualSubmitModalRecord(null)
              setManualClaimNumber('')
              setManualFormNumber('')
            }
          }}
          title="Mark claim as submitted (manual)"
        >
          <p className="stc-report-submit-modal-intro">
            Use this when the claim was submitted directly in PCRS. Saving will set status to To Pay and apply the same downstream rules as standard submission.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (!manualSubmitModalRecord) return
              const claimNumber = manualClaimNumber.trim()
              const formNumber = manualFormNumber.trim()
              if (!claimNumber) return
              manualSubmitMutation.mutate({
                id: manualSubmitModalRecord.id,
                claimNumber,
                ...(formNumber ? { formNumber } : {}),
              })
            }}
            className="stc-report-submit-form"
          >
            <div className="form-group">
              <label htmlFor="manual-claim-number">Claim number</label>
              <input
                id="manual-claim-number"
                type="text"
                value={manualClaimNumber}
                onChange={(e) => setManualClaimNumber(e.target.value)}
                disabled={manualSubmitMutation.isPending}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="manual-form-number">Form number (optional)</label>
              <input
                id="manual-form-number"
                type="text"
                value={manualFormNumber}
                onChange={(e) => setManualFormNumber(e.target.value)}
                disabled={manualSubmitMutation.isPending}
              />
            </div>
            <div className="stc-report-submit-form-actions">
              <button
                type="button"
                onClick={() => {
                  setManualSubmitModalRecord(null)
                  setManualClaimNumber('')
                  setManualFormNumber('')
                }}
                disabled={manualSubmitMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={manualSubmitMutation.isPending || !manualClaimNumber.trim()}
              >
                {manualSubmitMutation.isPending ? 'Saving…' : 'Set to Submitted'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {rejectModalRecord && (
        <Modal
          isOpen={!!rejectModalRecord}
          onClose={() => {
            if (!rejectMutation.isPending) {
              setRejectModalRecord(null)
              setRejectReasonPreset('')
              setRejectReasonOther('')
              setRejectReasonDetails('')
            }
          }}
          title="Reject claim"
        >
          <p className="stc-report-submit-modal-intro">
            Please provide a reason for rejecting this claim. The reason will be visible when hovering over the status and in the claim detail.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (rejectModalRecord) {
                const baseReason =
                  rejectReasonPreset === 'other'
                    ? rejectReasonOther.trim()
                    : REJECTION_REASON_OPTIONS.find((o) => o.value === rejectReasonPreset)?.label ?? rejectReasonPreset
                const details = rejectReasonDetails.trim()
                const reason = details ? `${baseReason}. ${details}` : baseReason
                rejectMutation.mutate({
                  id: rejectModalRecord.id,
                  rejectionReason: reason || undefined,
                })
              }
            }}
            className="stc-report-submit-form"
          >
            <div className="form-group">
              <label htmlFor="reject-reason-preset">
                Reason for rejection <span className="required">(required)</span>
              </label>
              <select
                id="reject-reason-preset"
                value={rejectReasonPreset}
                onChange={(e) => setRejectReasonPreset(e.target.value)}
                required
                disabled={rejectMutation.isPending}
                aria-describedby="reject-reason-hint"
              >
                <option value="">Select a reason...</option>
                {REJECTION_REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {rejectReasonPreset === 'other' && (
                <textarea
                  id="reject-reason-other"
                  value={rejectReasonOther}
                  onChange={(e) => setRejectReasonOther(e.target.value)}
                  placeholder="Enter your reason..."
                  rows={3}
                  required
                  disabled={rejectMutation.isPending}
                  className="stc-report-reject-other-input"
                  aria-label="Other rejection reason"
                />
              )}
              {rejectReasonPreset && rejectReasonPreset !== 'other' && (
                <textarea
                  id="reject-reason-details"
                  value={rejectReasonDetails}
                  onChange={(e) => setRejectReasonDetails(e.target.value)}
                  placeholder="Add more details (optional)"
                  rows={2}
                  disabled={rejectMutation.isPending}
                  className="stc-report-reject-details-input"
                  aria-label="Additional details"
                />
              )}
              <span id="reject-reason-hint" className="form-hint" role="status">
                This reason will be shown as a tooltip on the Rejected status and in the claim detail view.
              </span>
            </div>
            {rejectMutation.isError && (
              <p className="form-error" role="alert">
                {(rejectMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
                  (rejectMutation.error instanceof Error ? rejectMutation.error.message : 'Failed to reject claim. Please try again.')}
              </p>
            )}
            <div className="stc-report-submit-form-actions">
              <button
                type="button"
                onClick={() => {
                setRejectModalRecord(null)
                setRejectReasonPreset('')
                setRejectReasonOther('')
                setRejectReasonDetails('')
                }}
                disabled={rejectMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  rejectMutation.isPending ||
                  !rejectReasonPreset ||
                  (rejectReasonPreset === 'other' && !rejectReasonOther.trim())
                }
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject claim'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {resetToSignedModalRecord && (
        <Modal
          isOpen={!!resetToSignedModalRecord}
          onClose={() => {
            if (!resetToSignedMutation.isPending) {
              setResetToSignedModalRecord(null)
            }
          }}
          title="Return claim to signed?"
        >
          <p className="stc-report-submit-modal-intro">
            This will move the claim from Failed to Signed so it can be reviewed, edited, queued, or submitted again.
          </p>
          {resetToSignedModalRecord.lastSubmissionErrorMessage && (
            <p className="form-hint" role="status">
              Last submission error: {resetToSignedModalRecord.lastSubmissionErrorMessage}
            </p>
          )}
          <div className="stc-report-submit-form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setResetToSignedModalRecord(null)}
              disabled={resetToSignedMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (!resetToSignedModalRecord) return
                resetToSignedMutation.mutate(resetToSignedModalRecord.id, {
                  onSuccess: () => setResetToSignedModalRecord(null),
                })
              }}
              disabled={resetToSignedMutation.isPending}
            >
              {resetToSignedMutation.isPending ? 'Returning…' : 'Return to signed'}
            </button>
          </div>
        </Modal>
      )}

      {batchModalOpen && (
        <Modal
          isOpen={batchModalOpen}
          onClose={() => {
            if (!startBatchMutation.isPending) {
              setBatchModalOpen(false)
              setBatchSelectedProviderId('')
            }
          }}
          title="Run queued claims batch"
        >
          <p className="stc-report-submit-modal-intro">
            Choose the submitting provider for this batch. All queued claims on this page will be locked while the run is active.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const selected = providersWithGms.find((p) => p.id === batchSelectedProviderId)
              if (!selected?.gmsNumber?.trim()) return
              startBatchMutation.mutate({
                docNum: selected.gmsNumber.trim(),
                docNam: formatDoctorNameForClaim(selected),
              })
            }}
            className="stc-report-submit-form"
          >
            <div className="form-group">
              <label htmlFor="batch-provider">Submitting provider (GMS number required)</label>
              <select
                id="batch-provider"
                value={batchSelectedProviderId}
                onChange={(e) => setBatchSelectedProviderId(e.target.value)}
                required
                disabled={startBatchMutation.isPending}
              >
                <option value="">Select provider...</option>
                {providersWithGms.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {formatUserName(provider)} (GMS: {provider.gmsNumber})
                  </option>
                ))}
              </select>
            </div>
            <div className="stc-report-submit-form-actions">
              <button
                type="button"
                onClick={() => {
                  setBatchModalOpen(false)
                  setBatchSelectedProviderId('')
                }}
                disabled={startBatchMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={startBatchMutation.isPending || !batchSelectedProviderId || providersWithGms.length === 0 || queuedRecords.length === 0}
              >
                {startBatchMutation.isPending ? 'Starting…' : `Start batch (${queuedRecords.length})`}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {selectedRecord && (
        <Modal
          isOpen={!!selectedRecord}
          onClose={() => setSelectedRecord(null)}
          title={`STC / MICS – ${selectedRecord.patientName ?? selectedRecord.patientId}`}
          size="large"
        >
          <StcSigning
            embedded
            consultationIdProp={selectedRecord.consultationId}
            recordIdProp={selectedRecord.id}
            patientIdProp={selectedRecord.patientId}
            patientNameProp={selectedRecord.patientName ?? undefined}
            formTypeProp={selectedRecord.formType}
            readOnly={READ_ONLY_STATUSES.has(selectedRecord.status)}
            onClose={() => setSelectedRecord(null)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['stc-records'] })
              setSelectedRecord(null)
            }}
          />
        </Modal>
      )}
    </div>
  )
}
